import { countriesList } from '@cmg/common';
import defaultTo from 'lodash/defaultTo';

import { useGetOfferingCountryCode } from '../../../../../../../../common/hooks/useGetOfferingCountryCode';
import { AttestationFormStatus, CertificateType } from '../../../../../../../../graphql';
import offeringSidePanelBuySideRoutes from '../../../../../../offeringSidePanelBuySideRouteFactory';
import { OrderBookPersonaType } from '../../../../../hooks/useIdentifyOrderBookPersona';
import { useBuySideAttestationQuery } from '../../buy-side/useAttestationQuery';
import { CertificateTypeLabels } from '../Certificates.model';
import { CertificateModel, PersonaProps } from '../useCertificatesModel';

export type Props = PersonaProps & {
  offeringId: string;
  certificateType?: CertificateType | null;
  hasActiveRelationship?: boolean;
};

export const COMPLIANCE_WARNING_MESSAGE_BUYSIDE =
  'To submit and modify indications, review the outstanding requirements in the Compliance section. For support, contact your broker relationship(s).';

export const COMPLIANCE_TOOLTIP_WARNING_MESSAGE_BUYSIDE = `is required to participate in this offering. Click Copy link to submit the necessary certificates via the provided link.`;

export function getComplianceMessages(certificateType: CertificateType) {
  return {
    tooltipMessage: `${CertificateTypeLabels[certificateType]} ${COMPLIANCE_TOOLTIP_WARNING_MESSAGE_BUYSIDE}`,
  };
}

export const useBuySideCertificatesModel = ({
  type,
  offeringId,
  certificateType,
  hasActiveRelationship = false,
}: Props): CertificateModel => {
  const isBuySide = type === OrderBookPersonaType.BUY_SIDE;
  const { tooltipMessage } = getComplianceMessages(certificateType!);
  const offeringCountryCode = useGetOfferingCountryCode(offeringId);
  const isUsOffering = offeringCountryCode === countriesList.Usa.countryCode;
  const attestationQuery = useBuySideAttestationQuery(
    { offeringId, certificateType: certificateType! },
    { skip: !isBuySide || !offeringId || !certificateType || !hasActiveRelationship }
  );
  let myAttestation = defaultTo(attestationQuery.data?.myAttestation, null);
  const offeringType = attestationQuery.data?.basicPublishedOffering.type;

  if (!isUsOffering && myAttestation?.certificateType === CertificateType.Form51305131) {
    myAttestation = null;
  }

  const hasComplianceWarning =
    myAttestation?.formStatus === AttestationFormStatus.Expired ||
    myAttestation?.formStatus === AttestationFormStatus.NotOnFile;
  const redirectUrl = offeringSidePanelBuySideRoutes.orderBookCompliance.getUrlPath();

  return {
    canRead: isBuySide && !!myAttestation,
    attestation: myAttestation,
    attestationForm: myAttestation?.attestationForm ?? null,
    loading: attestationQuery.loading,
    offeringType: offeringType,
    certificateType,
    warning: {
      hasComplianceWarning,
      canByPassComplianceWarning: false,
      message: COMPLIANCE_WARNING_MESSAGE_BUYSIDE,
      redirectUrl,
      tooltipMessage,
    },
  };
};
